import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

const LEVEL_L1 = 1;
const LEVEL_L2 = 2;
const LEVEL_L3 = 3;
const LEVEL_LOCATION = 4;

const DEFAULT_OPTIONS = [
  {
    value: LEVEL_L1,
    label: 'Category',
    labelShort: 'Categ',
    title: 'Table starting at category (L1)',
  },
  {
    value: LEVEL_L2,
    label: 'Sub-Category',
    labelShort: 'Sub',
    title: 'Table starting at sub-category (L2)',
  },
  {
    value: LEVEL_L3,
    label: 'Asset',
    labelShort: 'Asset',
    title: 'Assets only (L3)',
  },
  {
    value: LEVEL_LOCATION,
    label: 'Location',
    labelShort: 'Location',
    title: 'Group by location',
  },
];

/**
 * Mobile-only control: single pill chip (same “selected segment” look as Assets header)
 * opens a dark single-select menu. Scoped to table start level (L1 / L2 / L3 / location).
 * Options: `label` = full text in the menu; optional `labelShort` = compact chip text (defaults to `label`).
 */
const DropdownTableStartLevelChip = ({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  chipWidthClass = 'w-[6.25rem] min-w-[6.25rem]',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((o) => o.value === value) ?? options[0];
  const chipText = selected.labelShort ?? selected.label;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const portalStyle = () => {
    const el = wrapRef.current;
    if (!el) return {};
    const r = el.getBoundingClientRect();
    return {
      position: 'fixed',
      top: r.bottom + 4,
      left: r.left,
      minWidth: r.width,
      zIndex: 50,
    };
  };

  const handlePick = (v) => {
    onChange?.(v);
    setOpen(false);
  };

  return (
    <div className={`relative flex justify-center ${className}`} ref={wrapRef}>
      {/* Same shell as segmented control: gray track + single green “segment” */}
      <div className={`rounded-full bg-gray-500 bg-opacity-30 p-0.5 ${chipWidthClass}`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title={selected.title}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Table starting level"
          className="flex w-full items-center justify-between gap-0.5 rounded-full bg-green-400 px-1.5 py-1 text-left text-xs font-medium text-white transition-opacity hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-40"
        >
          <span className="min-w-0 flex-1 truncate text-center">{chipText}</span>
          <ChevronDownIcon
            className={`h-3.5 w-3.5 flex-shrink-0 text-white opacity-90 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-activedescendant={String(value)}
            style={portalStyle()}
            className="min-w-[10.5rem] rounded-md border border-gray-600 bg-gray-700 py-1 shadow-lg ring-1 ring-black/20"
          >
            {options.map((opt) => {
              const isOn = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isOn}
                  title={opt.title}
                  onClick={() => handlePick(opt.value)}
                  className={`flex w-full cursor-pointer items-center px-3 py-1.5 text-left text-xs transition-colors ${
                    isOn ? 'bg-gray-600 font-medium text-green-400' : 'text-white hover:bg-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default DropdownTableStartLevelChip;
